import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { apiGet, apiPut, operationsClient } from '@/lib/api';
import { leadRepAvailabilityEndpoints } from '@/lib/api/endpoints';
import { leadRepAvailabilityKeys } from '@/lib/queryKeys';

export interface WeeklyTimeRange {
  startTime: string; // HH:mm
  endTime: string; // HH:mm
}

export interface WeeklyDay {
  dayOfWeek: number; // 0 = Sun .. 6 = Sat
  isWorking: boolean;
  timeRanges: WeeklyTimeRange[];
}

export interface WeeklySchedule {
  timezone: string;
  days: WeeklyDay[];
}

/** A rep's weekly working-hours schedule (used when auto-assign honors hours). */
export function useLeadRepSchedule(userId: string | null) {
  return useApiQuery<WeeklySchedule>({
    queryKey: leadRepAvailabilityKeys.schedule(userId ?? ''),
    queryFn: () =>
      apiGet<WeeklySchedule>(
        operationsClient,
        leadRepAvailabilityEndpoints.schedule(userId as string),
      ),
    enabled: Boolean(userId),
    showErrorToast: false,
  });
}

export function useUpdateLeadRepSchedule() {
  const queryClient = useQueryClient();

  return useApiMutation<WeeklySchedule, { userId: string } & WeeklySchedule>({
    mutationFn: ({ userId, ...schedule }) =>
      apiPut<WeeklySchedule, WeeklySchedule>(
        operationsClient,
        leadRepAvailabilityEndpoints.schedule(userId),
        schedule,
      ),
    onSuccess: (_data, { userId }) => {
      void queryClient.invalidateQueries({
        queryKey: leadRepAvailabilityKeys.schedule(userId),
      });
      void queryClient.invalidateQueries({
        queryKey: leadRepAvailabilityKeys.list(),
      });
      toast.success('Working hours updated');
    },
  });
}
